import { Clock, FileText, History, Send } from "lucide-react";
import React from "react";
import { NavLink, useLocation } from "react-router-dom";

import type { Patient } from "@elation/api-client/src/stitch-web";

import { useIsChromeExtension } from "@src/features/chrome";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";
import { cn } from "@src/shared/lib/utils";
import { navigation } from "@src/shared/navigation";
import { CitationsProvider } from "@src/shared/providers/citationsProviders";

import { AIScribe, type AIScribeProps } from "./components/AIScribe/AIScribe";

export interface PatientLayoutProps {
  patient: Patient;
  aiScribe: AIScribeProps;
}

/**
 * Floating "ask a question" input rendered above the patient toolbar.
 *
 * Kept around so we can re-enable it once the conversational answer flow is
 * ready; intentionally not rendered in the layout below in v0.
 */
export const AskQuestionBar = () => {
  return (
    <div className="pointer-events-auto relative w-full max-w-[min(920px,calc(100%-32px))]">
      <Input
        placeholder="Ask a question..."
        aria-label="Ask a question"
        className="h-12 rounded-full border border-border bg-background pl-5 pr-14 text-base shadow-card"
      />
      <Button
        size="icon"
        aria-label="Send"
        className="absolute right-1.5 top-1/2 size-9 -translate-y-1/2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <Send className="size-4" />
      </Button>
    </div>
  );
};

const PatientLayoutContent = ({
  patient,
  aiScribe,
  children,
}: React.PropsWithChildren<PatientLayoutProps>) => {
  const isChromeExtension = useIsChromeExtension();
  const { pathname } = useLocation();
  const patientId = String(patient.id);
  const briefingPath = navigation.patientBriefing(patientId);
  const todayPath = navigation.patientToday(patientId);
  const encountersPath = navigation.patientEncounters(patientId);
  const isBriefing =
    pathname === briefingPath || pathname.endsWith("/briefing");
  const isToday = pathname === todayPath || pathname.endsWith("/today");
  const isEncounters =
    pathname === encountersPath || pathname.endsWith("/encounters");

  return (
    <VerticalFlexbox className="relative min-h-0 flex-1">
      <VerticalFlexbox className="min-h-0 flex-1 overflow-y-auto pb-32">
        {children}
      </VerticalFlexbox>
      <VerticalFlexbox
        role="toolbar"
        gap="sm"
        className={cn(
          "pointer-events-none fixed bottom-6 left-0 right-0 z-10 flex flex-col items-center gap-sm px-2 sm-stitch:px-4",
          !isChromeExtension && "md-stitch:left-64 md-stitch:right-0",
        )}
      >
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="space-between"
          gap="sm"
          flexWrap="wrap"
          className="pointer-events-auto w-full max-w-[min(920px,calc(100%-16px))] justify-center rounded-2xl border border-border bg-background/95 p-2 shadow-elevated backdrop-blur-md sm-stitch:flex-nowrap sm-stitch:justify-between"
        >
          <HorizontalFlexbox
            alignItems="center"
            justifyContent="center"
            gap="xs"
            flexWrap="wrap"
            className="min-w-0 sm-stitch:flex-nowrap"
          >
            <Button
              asChild
              variant={isBriefing ? "default" : "ghost"}
              aria-label="Briefing"
              className="px-2 sm-stitch:px-3"
            >
              <NavLink to={briefingPath}>
                <FileText />
                <span>Briefing</span>
              </NavLink>
            </Button>
            <Button
              asChild
              variant={isToday ? "default" : "ghost"}
              aria-label="Today"
              className="px-2 sm-stitch:px-3"
            >
              <NavLink to={todayPath}>
                <Clock />
                <span>Today</span>
              </NavLink>
            </Button>
            <Button
              asChild
              variant={isEncounters ? "default" : "ghost"}
              aria-label="Encounters"
              className="px-2 sm-stitch:px-3"
            >
              <NavLink to={encountersPath}>
                <History />
                <span>Encounters</span>
              </NavLink>
            </Button>
          </HorizontalFlexbox>
          <AIScribe {...aiScribe} />
        </HorizontalFlexbox>
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
};

export const PatientLayout = ({
  children,
  patient,
  aiScribe,
}: React.PropsWithChildren<PatientLayoutProps>) => {
  return (
    <CitationsProvider>
      <PatientLayoutContent patient={patient} aiScribe={aiScribe}>
        {children}
      </PatientLayoutContent>
    </CitationsProvider>
  );
};
